import * as React from 'react';
import propz from 'propz';
import BPromise from 'bluebird';
import { parse } from 'query-string';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Component } from 'react';
import { Child } from 'Src/models/child';
import { EventInvitationMessage } from 'Src/models/message';
import { CARD_LIMIT } from 'Src/consts/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { getChildren } from 'Src/helpers/service/parent/children';
import { getParentArchiveMessages, getParentArchiveMessagesCount } from 'Src/helpers/service/parent/messagesTable';
import { ParentFilterBar } from 'Src/components/ParentFilterBar/ParentFilterBar';
import { AppliedTextFilterChip } from 'Src/components/AppliedTextFilterChip/AppliedTextFilterChip';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import { Loader } from 'Src/components/Loader/Loader';
import { AbsenceRequestCard } from 'Src/views/GenericView/ParentView/AbsenceRequests/AbsenceRequestCard/AbsenceRequestCard';
import { ParentPageHeader } from '../../components/ParentPageHeader';
import '../../components/ParentLists.scss';
import { ItemsBar } from '../../Calendar/ItemsBar';
import moment from 'moment';
import { AbsenceRequestDetails } from '../AbsenceRequestDetails/AbsenceRequestDetails';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  items: EventInvitationMessage[];
  selectedItem: EventInvitationMessage;
  total: number;
  currentPage: number;
  filters: any;
  sortFieldName: string;
  sortDirection: string;
  isFiltersOpen: boolean;
  isLoading: boolean;
  children: Child[];
  basePath: string;
  isShowDetailsModalOpen: boolean;
}

const FILTER_FIELDS: ColumnDefinition[] = [
  {
    text: 'Children',
    field: 'childIdList',
    type: FILTER_TYPE.MULTISELECT,
    accessor: [''],
    isSort: false
  },
  {
    text: 'Details',
    field: 'details',
    type: FILTER_TYPE.TEXT,
    accessor: [''],
    isSort: false
  },
  {
    text: 'Start date',
    field: 'dateAbsence',
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: [''],
    isSort: true
  }
];

const currentAcademicYearStart = () => {
  const now = moment();
  const startYear = now.month() >= 8 ? now.year() : now.year() - 1;
  return moment([startYear, 8, 1]);
};

const absenceDateRange = (value: any) => ({
  ...(value?.from
    ? {
        $gte: moment(value.from)
          .startOf('day')
          .toISOString()
      }
    : {}),
  ...(value?.to
    ? {
        $lte: moment(value.to)
          .endOf('day')
          .toISOString()
      }
    : {})
});

export class AbsenceRequestsArchive extends Component<Props, State> {
  private loadRequestId = 0;

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      selectedItem: undefined,
      total: 0,
      currentPage: FIRST_PAGE,
      filters: {},
      sortFieldName: '',
      sortDirection: '',
      isFiltersOpen: false,
      isLoading: false,
      basePath: '',
      children: [],
      isShowDetailsModalOpen: false
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  setItems(props: Props = this.props) {
    const requestId = ++this.loadRequestId;
    const { user, location } = props;

    const search = parse(location.search);

    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortFieldName = '';

    if (typeof search.order === 'string') {
      [sortFieldName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortFieldName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(FILTER_FIELDS, search);
    const hasAppliedArchiveFilters = Object.keys(search).some(key => key !== 'page' && key !== 'order');

    if (!hasAppliedArchiveFilters) {
      return getChildren(user).then(children => {
        if (requestId !== this.loadRequestId) return;
        this.setState({
          items: [],
          currentPage: FIRST_PAGE,
          children,
          filters,
          sortDirection,
          sortFieldName,
          total: 0,
          basePath: location.pathname,
          isLoading: false
        });
      });
    }

    const where = {
      kind: 'AbsenceMessage',
      ...getServerFieldSectionWhere(filters),
      dateAbsence:
        filters.dateAbsence && (filters.dateAbsence.from || filters.dateAbsence.to)
          ? absenceDateRange(filters.dateAbsence)
          : {
              $lte: currentAcademicYearStart()
                .subtract(1, 'day')
                .endOf('day')
                .toISOString()
            }
    };

    const order = sortByFieldExist && sortDirectionExist ? `${sortFieldName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order, CARD_LIMIT);

    const getItemsPromise = getParentArchiveMessages(user, serverQueryFilter);
    const getItemsCountPromise = getParentArchiveMessagesCount(user, where);
    const getChildrenPromise = getChildren(user);

    const promises = [getItemsPromise, getItemsCountPromise, getChildrenPromise];

    return BPromise.all(promises).then(([clubBookings, clubBookingsCountObj, children]) => {
      if (requestId !== this.loadRequestId) return;
      this.setState({
        items: clubBookings,
        currentPage: page,
        children,
        filters,
        sortDirection,
        sortFieldName,
        total: clubBookingsCountObj.count,
        basePath: location.pathname,
        isLoading: false
      });
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const { history } = this.props;
    let search: string[] = [];

    const { filters, sortFieldName, sortDirection, basePath } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortFieldName)) {
      search.push(getSearchOrder(sortDirection, sortFieldName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortFieldName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortFieldName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = propz.get(event, ['target', 'value'], '');
    const { filters } = this.state;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = FILTER_FIELDS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortFieldName } = this.state;
    const { history } = this.props;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortFieldName)) {
      search.push(getSearchOrder(sortDirection, sortFieldName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onChildPickerClick = (childId: string): void => {
    const { children } = this.state;
    const filters = {
      ...this.state.filters,
      childIdList: childId === 'all' ? children.map(child => child.id) : [childId]
    };
    this.setState({ filters }, this.onApplyFilterClick);
  };

  renderShowDetailsModal = () => {
    const { isShowDetailsModalOpen, selectedItem } = this.state;
    return (
      <SimpleModal
        isOpen={isShowDetailsModalOpen}
        onCloseClick={this.closeShowDetailsModal}
        title="Absence request"
        side="right"
        showFooter={false}
      >
        {selectedItem && <AbsenceRequestDetails item={selectedItem as any} />}
      </SimpleModal>
    );
  };

  openShowDetailsModal = message => {
    this.setState({
      isShowDetailsModalOpen: true,
      selectedItem: message
    });
  };

  closeShowDetailsModal = () => {
    this.setState({
      isShowDetailsModalOpen: false,
      selectedItem: undefined
    });
  };

  render() {
    const { isLoading, isFiltersOpen, total, currentPage, items, filters, children } = this.state;
    const archiveSearch = parse(this.props.location.search);
    const hasAppliedArchiveFilters = Object.keys(archiveSearch).some(key => key !== 'page' && key !== 'order');

    const childIdList = children.map(child => child.id);

    if (isLoading) {
      return <Loader />;
    }

    return (
      <main className="parentPage parentListPage parentAbsencePage">
        {this.renderShowDetailsModal()}

        <ParentPageHeader
          title="Absence requests"
          subtitle="Previous absence notifications sent to the school."
          tabs={[
            { label: 'Current', onClick: () => this.props.history.push('/absenceRequests/outbox') },
            {
              label: 'Archive',
              active: true,
              count: total,
              onClick: () => this.props.history.push('/absenceRequests/archive')
            }
          ]}
          chips={[{ value: total, label: total === 1 ? 'archived request' : 'archived requests', tone: 'slate' }]}
        />

        <ItemsBar
          items={children}
          itemIdList={filters.childIdList && filters.childIdList.length ? filters.childIdList : childIdList}
          onClick={this.onChildPickerClick}
          allItemsTitle="All children"
          getItemName={(child: Child) => `${child.firstName} ${child.lastName}`}
        />

        <div className="parentListControls">
          <button
            type="button"
            className={isFiltersOpen ? 'parentFilterButton is-active' : 'parentFilterButton'}
            onClick={() => this.setState(prev => ({ isFiltersOpen: !prev.isFiltersOpen }))}
          >
            <span className="parentFilterButton__icon" aria-hidden="true" /> Filters
          </button>

          <Pagination2
            currentPage={currentPage}
            total={total}
            setCurrentPageParams={this.setCurrentPageParams}
            limit={CARD_LIMIT}
            modern
          />
        </div>

        <AppliedTextFilterChip
          history={this.props.history}
          location={this.props.location}
          filters={[
            { filterKey: 'details', label: 'Details' },
            { filterKey: 'dateAbsence', label: 'Absence date', type: 'date' }
          ]}
        />

        {isFiltersOpen && (
          <div className="parentFilterSurface">
            <ParentFilterBar
              filters={filters}
              onTableFilterChange={this.onTableFilterChange}
              onApply={this.onApplyFilterClick}
              onClear={this.onClearFilterClick}
              onCancel={() => this.setState({ isFiltersOpen: false })}
              labels={{ text: 'Details', date: 'Absence date' }}
              fieldKeys={{ textKey: 'details', dateKey: 'dateAbsence' }}
            />
          </div>
        )}

        {items.length === 0 ? (
          <div className="parentListEmpty">
            <strong>{hasAppliedArchiveFilters ? 'No absence requests match your filters' : 'Search the archive'}</strong>
            <span>
              {hasAppliedArchiveFilters
                ? 'Try changing the absence date range or removing one of the other filters.'
                : 'Use the absence date and other filters to find an older request.'}
            </span>
          </div>
        ) : (
          <div className="parentListGrid">
            {items.map(item => {
              const { id } = item;

              return (
                <div className="parentListGrid__item" key={id}>
                  <AbsenceRequestCard item={item} onShowDetailsClick={this.openShowDetailsModal} />
                </div>
              );
            })}
          </div>
        )}
      </main>
    );
  }
}
